import type { CreateJobInput, UpdateJobInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError } from '../../../shared/shared.api';
import { useToast } from '../../../shared/hooks/use-toast';
import { createJob, getJob, updateJob, type JobDetail } from '../jobs.api';

/** `job: null` means a not-yet-created job — quick-create's blank state. */
export type JobRecordState =
  { status: 'loading' } | { status: 'ready'; job: JobDetail | null } | { status: 'forbidden' } | { status: 'not_found' } | { status: 'error' };

function toErrorState(err: unknown): JobRecordState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not_found' };
  return { status: 'error' };
}

/**
 * A job's own page: fetch-and-fill-in-later when `id` is set, quick-create's blank
 * state when it isn't. Mount with `key={id ?? 'new'}` (JobIntakePage), same idiom as
 * `useZoneEditor`. A save failure stays inline; create toasts and navigates to the
 * job's own page, update toasts and reloads in place — there's no list to return to.
 */
export function useJobRecord(id: string | undefined) {
  const [state, setState] = useState<JobRecordState>(id ? { status: 'loading' } : { status: 'ready', job: null });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getJob(id)
      .then((job) => {
        if (!cancelled) setState({ status: 'ready', job });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function reload(): Promise<void> {
    if (!id) return;
    try {
      setState({ status: 'ready', job: await getJob(id) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  async function create(input: CreateJobInput): Promise<void> {
    setSaveError(null);
    setSaving(true);
    try {
      const job = await createJob(input);
      toast.success(`Job ${job.number} created`);
      navigate(`/jobs/${job.id}`, { replace: true });
    } catch {
      setSaveError('Could not create — check the fields and try again.');
    } finally {
      setSaving(false);
    }
  }

  async function update(input: UpdateJobInput): Promise<void> {
    if (!id) return;
    setSaveError(null);
    setSaving(true);
    try {
      const job = await updateJob(id, input);
      await reload();
      toast.success(`Job ${job.number} updated`);
    } catch {
      setSaveError('Could not save — check the fields and try again.');
    } finally {
      setSaving(false);
    }
  }

  return { state, saveError, saving, create, update, reload };
}
